import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    Grid,
    InputAdornment,
    TextareaAutosize,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import useFetchRefundReasons from 'src/hooks/useFetchRefundReasons';
import { Autocomplete } from '@material-ui/lab';
import useUserIpAddress from 'src/hooks/useUserIpAddress';
import { useAssetSettings } from "src/hooks/useUserSettings";

// Hardcoded feature identifier for short term use
const refundfeatureIdentifier = 'baseFeature-850102f7-e0d8-457d-9af7-e30ee8cb8bca';

const useStyles = makeStyles((theme) => ({
    dialogTitleConatiner: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
    },
    dialogTitleText: {
        color: theme.palette.getContrastText(theme.palette.primary.dark),
        fontSize: theme.typography.h3.fontSize,
    },
    datePickerContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    actionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'right',
    },
    valueField: {
        width: '150px',
        textAlign: 'right',
    },
}));

function RefundDialog({ isDialogOpen, setIsDialogOpen, handleClose, retailPayment, onSuccess, retailPaymentService
}) {
    const { ipAddress, isLoading } = useUserIpAddress();
    const { email } = useSelector((state) => state.account.user);
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [filteredReasons, setFilteredReasons] = useState([]);
    const { reasons } = useFetchRefundReasons();
    const { hasFeatureAccess } = useAssetSettings();

    const maxRefundAmount = retailPayment.Refunds
        ? parseFloat((retailPayment.PaymentDetail.Total - retailPayment.Refunds.reduce((acc, refund) => acc + refund.RefundAmount, 0)).toFixed(2))
        : parseFloat(retailPayment.PaymentDetail.Total.toFixed(2));

    // Hardcoded Validation TODO: Replace with actual validation logic after release
    const userHasPermission = () => {
        if ( hasFeatureAccess(refundfeatureIdentifier) ) {
            return true;
        }
        return false;
    };

    const validationSchema = yup.object({
        retailPaymentId: yup.number().required('Retail Payment ID is required'),
        refundAmount: yup.number().required('Refund Amount is required')
            .min(1.00, 'Refund Amount must be greater than or equal to $1.00')
            .max(maxRefundAmount, 'Refund Amount must not exceed the total amount minus any previous refunds')
            .test('check-permission-over-100',  'Only refunds up to $100.00 allowed. Please contact Finance so that they can process the refund.', (value) => {
                if (value > 100){
                    return userHasPermission()
                }
                return true;
            }),
        refundReasonId: yup.number().required('Refund Reason is required'),
        refundUser: yup.string().required('Refund User is required'),
        refundUserIP: yup.string().required('Refund User IP is required'),
        refundNote: yup.string().nullable().when('refundReasonId', {
            is: (refundReasonId) => {
                const reason = reasons.find((r) => r.id === refundReasonId);
                return reason?.notesRequired; // Assuming `requiresNote` is a boolean property
            },
            then: yup.string().required('Refund Note is required for this reason'),
            otherwise: yup.string().nullable(),
        }),
    });

    const onSubmit = useCallback(async (values) => {
        try {
            await retailPaymentService?.createRetailPaymentRefund(values);
            setIsDialogOpen(false);
            formik.resetForm();
            enqueueSnackbar('Refund issued successfully', { variant: 'success' });

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            enqueueSnackbar(`Error issuing refund. ${error.message}`, { variant: 'error' });
        }
    }, [retailPayment]);

    const formik = useFormik({
        initialValues: {
            retailPaymentId: Number(retailPayment.RetailPaymentId),
            refundAmount: maxRefundAmount,
            refundReasonId: 0,
            refundUser: email,
            refundUserIP: '127.0.0.1',
            refundNote: null,
        },
        validationSchema: validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (reasons?.length > 0) {
            setFilteredReasons(reasons.filter((reason) => !reason.isInternal));
        }
    }, [reasons]);

    useEffect(() => {
        if (ipAddress) {
            formik.setFieldValue('refundUserIP', ipAddress);
        }
    }, [isLoading, ipAddress]);

    return (
        <Dialog
            open={isDialogOpen}
            onClose={(_, reason) => handleClose(reason)}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle
                className={classes.dialogTitleConatiner}
            >
                <Typography
                    className={classes.dialogTitleText}
                >
                    Refund Payment
                </Typography>
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2} fullWidth>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel>Refund Amount</FormLabel>
                                <TextField
                                    name="refundAmount"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (<InputAdornment position="start">$</InputAdornment>),
                                        inputProps: {
                                            step: 0.01,
                                            min: 1.00,
                                        },
                                    }}
                                    value={formik.values.refundAmount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.refundAmount)}
                                    helperText={formik.errors.refundAmount}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    fullWidth
                                    options={filteredReasons}
                                    onChange={(event, value) => {
                                        formik.setFieldValue('refundReasonId', value ? value.refundReasonId : 0);
                                    }}
                                    getOptionLabel={(option) => option.reason}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            label="Reason"
                                            variant="outlined"
                                            fullWidth
                                            name='refundReason'
                                            error={formik.touched.reason && Boolean(formik.errors.reason)}
                                            helperText={formik.touched.reason && formik.errors.reason}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel>Refund Note</FormLabel>
                            <TextareaAutosize
                                multiline="true"
                                minRows={6}
                                fullwidth
                                style={{ width: '100%' }}
                                name="refundNote"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.refundNote && Boolean(formik.errors.refundNote)}
                                helperText={formik.touched.refundNote && formik.errors.refundNote}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions
                    id="selected-records-actions"
                    data-test="selected-records-actions"
                    className={classes.dialogActions}
                >
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            setIsDialogOpen(false);
                            formik.resetForm();
                        }}
                        disabled={formik.isSubmitting}
                    >
                        Close
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                    >
                        {formik.isSubmitting && <CircularProgress size={24} style={{ marginRight: '12px' }} />}Create Refund
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

const RetailPaymentShape = PropTypes.shape({
    PoNumber: PropTypes.string.isRequired,
    RetailPaymentId:PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    DateCreated: PropTypes.string.isRequired,
    Caller: PropTypes.shape({
        FirstName: PropTypes.string.isRequired,
        LastName: PropTypes.string.isRequired,
        Email: PropTypes.string.isRequired,
    }).isRequired,
    StripePaymentId: PropTypes.string.isRequired,
    StripeInvoiceId: PropTypes.string.isRequired,
    StripeDisputeId: PropTypes.string,
    DisputeDate: PropTypes.string,
    PaymentDetail: PropTypes.shape({
        SubTotal: PropTypes.number.isRequired,
        Tax: PropTypes.number.isRequired,
        Total: PropTypes.number.isRequired,
        Currency: PropTypes.string.isRequired,
    }).isRequired,
    Refunds: PropTypes.arrayOf(PropTypes.shape({
        IsFullRefund: PropTypes.bool.isRequired,
        RefundAmount: PropTypes.number.isRequired,
        RefundDate: PropTypes.string.isRequired, // ISO date string
        RefundNote: PropTypes.string.isRequired,
        RefundReason: PropTypes.string.isRequired,
        RefundReasonId: PropTypes.number.isRequired,
        RefundUser: PropTypes.string.isRequired,
        RetailPaymentId: PropTypes.string.isRequired,
        RetailPaymentRefundId: PropTypes.string.isRequired,
        StripeRefundId: PropTypes.string.isRequired,
        TransactionId: PropTypes.string.isRequired,
    })).isRequired, // Assuming Refunds is an array of objects
});

RefundDialog.propTypes = {
    isDialogOpen: PropTypes.bool,
    setIsDialogOpen: PropTypes.func,
    handleClose: PropTypes.func,
    retailPayment: RetailPaymentShape.isRequired,
    onSuccess: PropTypes.func,
};


export default RefundDialog;
